import { get } from 'lodash';
import { handleAudioTranscript } from 'store/submissions/thunks';

export function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

export function isImageUrl(value) {
  if (!isHttpUrl(value)) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(value) || /\/image%2f/i.test(value);
}

export function isAudioUrl(value) {
  if (!isHttpUrl(value)) return false;
  return /\.(mp3|wav|m4a|aac|ogg|webm|mp4|mov)(\?|$)/i.test(value) || /\/(audio|video)%2f/i.test(value);
}

export function canonicalTranscriptUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
}

export function lookupTranscript(transcripts, url) {
  if (!url || typeof url !== 'string' || !transcripts) return null;
  const raw = url.trim();
  if (transcripts[raw]) return transcripts[raw];
  const canon = canonicalTranscriptUrl(raw);
  if (canon !== raw && transcripts[canon]) return transcripts[canon];
  const keys = Object.keys(transcripts);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (canonicalTranscriptUrl(key) === canon) return transcripts[key];
  }
  return null;
}

export function resolveMediaUrl(media) {
  if (!media) return '';
  if (typeof media === 'string') return media;
  if (Array.isArray(media)) {
    for (let i = 0; i < media.length; i += 1) {
      const nested = resolveMediaUrl(media[i]);
      if (nested) return nested;
    }
    return '';
  }
  if (typeof media === 'object') {
    return media.url || media.src || media.path || media.mediaUrl || '';
  }
  return '';
}

export function resolveGroupStemAudioUrl(question, allQuestions) {
  const direct = get(question, 'audio');
  if (direct && typeof direct === 'string' && direct.trim()) return direct.trim();
  const groupKey = question.groupKey;
  const qType = question.type;
  if (!groupKey || !Array.isArray(allQuestions)) return '';
  if (qType !== 'audioMultipleChoice' && qType !== 'audioEssay') return '';
  const match = allQuestions.find(
    (q) =>
      q &&
      q.groupKey === groupKey &&
      q.type === qType &&
      get(q, 'audio') &&
      typeof get(q, 'audio') === 'string' &&
      String(get(q, 'audio')).trim()
  );
  const a = get(match, 'audio');
  return typeof a === 'string' && a.trim() ? a.trim() : '';
}

export function normalizeMatchOption(option) {
  if (!option) return {};
  return option.value && typeof option.value === 'object' ? option.value : option;
}

export function pickMatchSideAudioUrl(normalized, side, questionType, matchType) {
  const isLeft = side === 'left';
  const leftMode = questionType || 'Text';
  const rightMode = matchType || 'Image';
  const mode = isLeft ? leftMode : rightMode;
  const pickHttp = (v) => {
    if (!v || typeof v !== 'string') return null;
    const t = v.trim();
    if (!isHttpUrl(t)) return null;
    if (isImageUrl(t)) return null;
    if (isAudioUrl(t)) return t;
    return t;
  };
  if (isLeft) {
    if (mode === 'Audio' || mode === 'ImageAndAudio') {
      return (
        pickHttp(normalized.questionAudio) ||
        pickHttp(normalized.match_audio) ||
        (typeof normalized.item === 'string' ? pickHttp(normalized.item) : null)
      );
    }
  } else if (mode === 'Audio' || mode === 'ImageAndAudio') {
    return pickHttp(normalized.match_audio) || (typeof normalized.match_item === 'string' ? pickHttp(normalized.match_item) : null);
  }
  return null;
}

export function collectMatchQuestionAudioUrls(question) {
  const urls = new Set();
  const questionType = get(question, 'questionType', 'Text');
  const matchType = get(question, 'matchType', 'Image');
  const options = get(question, 'options', []);
  if (!Array.isArray(options)) return urls;
  options.forEach((option) => {
    const normalized = normalizeMatchOption(option);
    const leftUrl = pickMatchSideAudioUrl(normalized, 'left', questionType, matchType);
    const rightUrl = pickMatchSideAudioUrl(normalized, 'right', questionType, matchType);
    if (leftUrl) urls.add(leftUrl);
    if (rightUrl) urls.add(rightUrl);
  });
  return urls;
}

function collectMediaUrlsForQuestion(question, answer, allQuestions) {
  const audioUrls = new Set();
  if (!question) return audioUrls;

  const questions = Array.isArray(allQuestions) && allQuestions.length ? allQuestions : [question];
  const questionType = question.type;
  const audioQuestionTypes = [
    'audioTrueFalse',
    'audioMultipleChoice',
    'audioEssay',
    'audio',
    'audioToImage',
    'twoMinAudio',
  ];

  if (audioQuestionTypes.includes(questionType)) {
    const questionAudio = resolveGroupStemAudioUrl(question, questions) || get(question, 'audio');
    if (questionAudio && typeof questionAudio === 'string' && questionAudio.trim()) {
      audioUrls.add(questionAudio.trim());
    }
  }

  if (questionType === 'video') {
    const questionVideo = get(question, 'video');
    if (questionVideo && typeof questionVideo === 'string' && questionVideo.trim() && isHttpUrl(questionVideo.trim())) {
      audioUrls.add(questionVideo.trim());
    }
  }

  if (questionType === 'match' || questionType === 'matchMaking') {
    collectMatchQuestionAudioUrls(question).forEach((u) => audioUrls.add(u));
  }

  if ((questionType === 'filling' || questionType === 'fillingImage') && get(question, 'fillingType') === 'Audio') {
    const fillingMedia = get(question, 'filling_image', []);
    if (Array.isArray(fillingMedia)) {
      fillingMedia.forEach((media) => {
        const mediaUrl = resolveMediaUrl(media);
        if (mediaUrl && typeof mediaUrl === 'string' && mediaUrl.trim()) {
          audioUrls.add(mediaUrl.trim());
        }
      });
    }
  }

  if (answer) {
    if (questionType === 'audio' || questionType === 'twoMinAudio') {
      const answerAudio = get(answer, 'answer');
      if (answerAudio && typeof answerAudio === 'string' && answerAudio.trim() && answerAudio.startsWith('http')) {
        audioUrls.add(answerAudio.trim());
      }
    }

    if (questionType === 'video') {
      const answerVideo = get(answer, 'answer');
      if (answerVideo && typeof answerVideo === 'string' && answerVideo.trim() && isHttpUrl(answerVideo.trim())) {
        audioUrls.add(answerVideo.trim());
      }
    }

    if (questionType === 'photoCollage') {
      const answerAudio = get(answer, 'answer_audio');
      if (answerAudio && typeof answerAudio === 'string' && answerAudio.trim()) {
        audioUrls.add(answerAudio.trim());
      }
    }

    if (questionType === 'picture' && get(question, 'categoryType') === 'Audio') {
      const answerAudio = get(answer, 'answer');
      if (answerAudio && typeof answerAudio === 'string' && answerAudio.trim() && answerAudio.startsWith('http')) {
        audioUrls.add(answerAudio.trim());
      }
    }
  }

  return audioUrls;
}

export function extractMediaUrlsForQuestion(question, answer, allQuestions) {
  return Array.from(collectMediaUrlsForQuestion(question, answer, allQuestions));
}

export function extractAudioUrls(questions, answers) {
  const audioUrls = new Set();

  if (!Array.isArray(questions)) {
    return [];
  }

  const answersList = Array.isArray(answers) ? answers : [];

  questions.forEach((question, index) => {
    collectMediaUrlsForQuestion(question, answersList[index], questions).forEach((u) => audioUrls.add(u));
  });

  return Array.from(audioUrls);
}

/** Build transcript map from getTranscript API rows + requested URLs */
export function mergeTranscriptRows(rows, requestedUrls = []) {
  const transcriptMap = {};
  if (!Array.isArray(rows)) return transcriptMap;

  rows.forEach((t) => {
    if (t.has_transcript && t.transcript) {
      transcriptMap[t.url] = t.transcript;
      const c = canonicalTranscriptUrl(t.url);
      if (c && c !== t.url) {
        transcriptMap[c] = t.transcript;
      }
    }
  });

  requestedUrls.forEach((req) => {
    const hit = rows.find(
      (t) =>
        t.has_transcript &&
        t.transcript &&
        (t.url === req || canonicalTranscriptUrl(t.url) === canonicalTranscriptUrl(req))
    );
    if (hit) {
      transcriptMap[req] = hit.transcript;
      const rc = canonicalTranscriptUrl(req);
      if (rc && rc !== req) {
        transcriptMap[rc] = hit.transcript;
      }
    }
  });

  return transcriptMap;
}

export function requestMediaTranscript(url) {
  if (!url || typeof url !== 'string' || !url.trim()) return undefined;
  return handleAudioTranscript(url.trim());
}

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function getMatchMediaData(option, side, questionType, matchType, transcripts = {}) {
  const normalized = normalizeMatchOption(option);
  const isLeft = side === 'left';
  const leftMode = questionType || 'Text';
  const rightMode = matchType || 'Image';
  const mode = isLeft ? leftMode : rightMode;

  if (isLeft) {
    if (mode === 'Audio') {
      const audioUrl = pickMatchSideAudioUrl(normalized, 'left', questionType, matchType);
      const transcript = audioUrl ? lookupTranscript(transcripts, audioUrl) : null;
      return {
        type: 'audio',
        url: audioUrl,
        text: transcript || '[Audio Response]',
        transcript: transcript || null,
      };
    }
    if (mode === 'Image') {
      const imageUrl = normalized.questionImage || (isImageUrl(normalized.item) ? normalized.item : null);
      if (imageUrl) {
        return { type: 'image', url: imageUrl };
      }
    }
    if (mode === 'ImageAndAudio') {
      const imageUrl = normalized.questionImage || normalized.match_item || (isImageUrl(normalized.item) ? normalized.item : null);
      const audioUrl = pickMatchSideAudioUrl(normalized, 'left', questionType, matchType);
      if (imageUrl || audioUrl) {
        const transcript = audioUrl ? lookupTranscript(transcripts, audioUrl) : null;
        return {
          type: 'imageAudio',
          url: imageUrl,
          audioUrl,
          text: transcript || '[Audio Response]',
          transcript: transcript || null,
        };
      }
    }
    const itemText = stripHtml(normalized.item || normalized.value || '');
    if (itemText && !isHttpUrl(itemText)) {
      return { type: 'text', text: itemText };
    }
  } else {
    if (mode === 'Audio') {
      const audioUrl = pickMatchSideAudioUrl(normalized, 'right', questionType, matchType);
      const transcript = audioUrl ? lookupTranscript(transcripts, audioUrl) : null;
      return {
        type: 'audio',
        url: audioUrl,
        text: transcript || '[Audio Response]',
        transcript: transcript || null,
      };
    }
    if (mode === 'Image') {
      const imageUrl = normalized.match_item || (isImageUrl(normalized.match_text) ? normalized.match_text : null);
      if (imageUrl && isImageUrl(imageUrl)) {
        return { type: 'image', url: imageUrl };
      }
    }
    if (mode === 'ImageAndAudio') {
      const imageUrl = normalized.match_item || null;
      const audioUrl = pickMatchSideAudioUrl(normalized, 'right', questionType, matchType);
      if (imageUrl || audioUrl) {
        const transcript = audioUrl ? lookupTranscript(transcripts, audioUrl) : null;
        return {
          type: 'imageAudio',
          url: imageUrl,
          audioUrl,
          text: transcript || '[Audio Response]',
          transcript: transcript || null,
        };
      }
    }
    const rightText = stripHtml(normalized.match_text || normalized.match_item || normalized.value || '');
    if (rightText && !isHttpUrl(rightText)) {
      return { type: 'text', text: rightText };
    }
  }

  return { type: 'text', text: '' };
}
